import _get from 'lodash/get';
import _merge from 'lodash/merge';
import { Lightning, Router } from '@lightningjs/sdk';
import Store from 'lightningjs-redux';
import { imageService, LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY } from '@bedrock-rdk/constants';
import { clearCache, fetchLayout } from '../../logic/layout/layout.actions';
import { fetchPlayerLayout } from '../../logic/player/player.actions';
import {
  PLAYBACK_STATE_ENDED,
  PLAYBACK_STATE_PAUSED,
  PLAYBACK_STATE_PLAYING,
  reset,
} from '../../logic/player/player.constants';
import {
  CONTROLS_HIDE_SIGNAL,
  CONTROLS_SHOW_SIGNAL,
  PAUSED_STATE,
  PLAYING_STATE,
  SEEK_SIGNAL,
  SIDE_GLASS_ITEM_SELECTED_SIGNAL,
  TOP_BAR_ITEM_SELECTED_SIGNAL,
} from '../player/interface/controls/controls.constants';
import { Controls } from '../player/interface/controls/controls.component';
import { TIME_BELOW, TIME_HIDDEN } from '../player/interface/progressBar/progressBar.constants';
import Spinner from '../tornado/spinner/spinner.component';
import { Autoscroll } from './autoscroll/autoscroll.component';
import { dispatchItemAction } from '../cta/cta.actions';
import { initAudioPlayer, getAudioPlayerInstance } from './audio.adapter';
import connect from './audio.connector';
import {
  COVER_SIZE,
  defaultContext,
  IMAGE_MARGIN,
  getExtraTitleFont,
  getInitialStyle,
  getTitleFont,
  TEXT_MARGIN,
  getControlsVisibleStyle,
  IMAGE_INITIAL_ALPHA,
  colors,
  getControlsHiddenStyle,
} from './audio.style';
import { getColors, getTopBarItems } from './audio.utils';
import { TOP_BAR_ITEMS } from './audio.constants';

export class AudioPlayer extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Background: {
          rect: true,
        },
        Content: {
          Cover: {
            Shadow: {
              type: Lightning.components.FastBlurComponent,
            },
            Image: {
              shader: {
                type: Lightning.shaders.RoundedRectangle,
              },
            },
          },
          Title: {
            type: Autoscroll,
          },
          ExtraTitle: {
            type: Autoscroll,
          },
        },
        Controls: {
          type: Controls,
          displayOverlayBackdrop: false,
          displayOverlayTop: false,
          displayMetadata: false,
          progressBarVariant: TIME_BELOW,
          signals: {
            [SEEK_SIGNAL]: '_handleSeek',
            [TOP_BAR_ITEM_SELECTED_SIGNAL]: '_handleTopBarItemSelection',
            [SIDE_GLASS_ITEM_SELECTED_SIGNAL]: '_handleItemAction',
            [CONTROLS_SHOW_SIGNAL]: '_onControlsShown',
            [CONTROLS_HIDE_SIGNAL]: '_onControlsHidden',
          },
          topBarItems: getTopBarItems(),
        },
        Spinner: {
          type: Spinner,
        },
      },
      getInitialStyle(),
    );
  }

  static _states() {
    return [
      class Playing extends this {
        _handlePauseControl() {
          getAudioPlayerInstance().pause();
          this.tag('Controls').show(PAUSED_STATE);
        }

        _handlePlayPause() {
          this._handlePauseControl();

          return true;
        }

        _handleEnter() {
          this._handlePauseControl();

          return true;
        }
      },
      class Paused extends this {
        $enter({ prevState }) {
          if (prevState) {
            this.tag('Controls').showControls();
          }
        }

        _handlePlayControl() {
          getAudioPlayerInstance().play();
          this.tag('Controls').show(PLAYING_STATE);
        }

        _handlePlayPause() {
          this._handlePlayControl();

          return true;
        }

        _handleEnter() {
          this._handlePlayControl();

          return true;
        }

        _handleBack() {
          getAudioPlayerInstance().pause();

          return false;
        }
      },
    ];
  }

  _build() {
    this._style = defaultContext;
    this._coverOffset = 0;
    this._autoHideTimeout = null;
  }

  _onUrlParams({ audioId }) {
    this._entityType = window.location.hash.replace('#', '').split('/').shift();
    this._entityId = audioId;

    const playerReady = initAudioPlayer(`${this._entityType}-player`);

    this._isResumed = this._clipId === this._entityId;
    this.tag('Controls').progressBarVariant = this._entityType === LAYOUT_ENTITIES.radio ? TIME_HIDDEN : TIME_BELOW;

    if (this._isResumed) {
      this.tag('Controls').showControls();
      this.tag('Spinner').alpha = 0;
      // NOTE: the radio engine is loaded on demand, so playback waits for it to be in place
      playerReady.then(player => {
        if (player) {
          player.play();
        }
      });
      // NOTE: this will prevent the _refocus chain from activating in the subsequent components
      Store.dispatch(clearCache(this._entityType, this._clipId, 'main'));
    } else {
      this.tag('Cover').alpha = IMAGE_INITIAL_ALPHA;
      this.tag('Spinner').setSmooth('alpha', 1);
      this._onControlsHidden();
      this.metadata = null;
      Store.dispatch(reset());
      Store.dispatch(fetchLayout(this._entityType, audioId));
    }
  }

  _attach() {
    this._unsubscribe = connect(this);
  }

  _detach() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }

    this._playbackState = undefined;
    this._hasRelatedEpisodes = false;

    this.tag('Image').removeAllListeners('txLoaded');
  }

  set canPlay(value) {
    if (value) {
      this.tag('Controls').showControls();
      this.tag('Spinner').setSmooth('alpha', 0);
    }
  }

  set playbackState(value) {
    switch (value) {
      case PLAYBACK_STATE_PLAYING:
        this._setState('Playing');

        break;
      case PLAYBACK_STATE_PAUSED:
        this._setState('Paused');

        break;
      case PLAYBACK_STATE_ENDED:
        if (this._playbackState === PLAYBACK_STATE_PLAYING || this._playbackState === PLAYBACK_STATE_PAUSED) {
          Router.back();
        }
        break;
      default:
        break;
    }

    this._playbackState = value;
  }

  set isBuffering(value) {
    this.tag('Spinner').setSmooth('alpha', value ? 1 : 0, { delay: 1 });
  }

  set metadata(value) {
    this._clipId = _get(value, 'video.id');
    const title = _get(value, 'title') || '';
    const extraTitle = _get(value, 'extraTitle') || '';
    const image = _get(value, 'image.idsByRatio.1:1') || _get(value, 'image.id') || '';

    const { screenSize, colorMode } = this._style;
    const coverSize = COVER_SIZE[screenSize];
    const imageSource = imageService.getUrl(image, coverSize, coverSize, {
      fit: IMAGE_FIT_SCALE_CROP,
      quality: IMAGE_QUALITY,
      format: DEFAULT_IMAGE_FORMAT,
    });
    const patch = {
      Content: {
        Cover: {
          Image: {
            src: imageSource,
          },
        },
        Title: { caption: title },
        ExtraTitle: { caption: extraTitle },
      },
    };

    let offset = COVER_SIZE[screenSize] + IMAGE_MARGIN[screenSize];

    if (title) {
      patch.Content.Title.y = offset;
      offset += getTitleFont(screenSize).lineHeight;
    }

    if (extraTitle) {
      offset += TEXT_MARGIN[screenSize];
      patch.Content.ExtraTitle.y = offset;
      offset += getExtraTitleFont(screenSize).lineHeight;
    }

    this._coverOffset = (offset - COVER_SIZE[screenSize]) / 2;
    patch.Content.h = offset;

    this.tag('Image').removeAllListeners('txLoaded');
    this.tag('Image').once('txLoaded', () => {
      this.tag('Cover').setSmooth('alpha', 1);

      getColors(this.stage, imageSource)
        .catch(() => [colors.background[colorMode], colors.background[colorMode]])
        .then(([bottom, top]) => {
          this.tag('Background').patch({
            smooth: {
              colorTop: top,
              colorBottom: bottom,
            },
          });
        });
    });

    this.patch(patch);
  }

  set hasRelatedEpisodes(value) {
    this._hasRelatedEpisodes = value;

    this.tag('Controls').topBarItems = getTopBarItems({
      hasRelatedEpisodes: this._hasRelatedEpisodes,
    });
  }

  _getFocused() {
    return this.tag('Controls');
  }

  _handleTopBarItemSelection(item) {
    const id = (item && item.id) || '';

    if (id === TOP_BAR_ITEMS.BACK) {
      Router.back();
    } else if (id === TOP_BAR_ITEMS.EPISODES) {
      this.tag('Controls').showSideGlass();
    }
  }

  _handleItemAction(item) {
    dispatchItemAction(item);
  }

  _onControlsShown() {
    this.patch(getControlsVisibleStyle(this._style.screenSize));
  }

  _onControlsHidden() {
    this.patch(getControlsHiddenStyle(this._style.screenSize, this._coverOffset));
  }

  _handleSeek(position) {
    getAudioPlayerInstance().seek(position);
  }

  start() {
    Store.dispatch(fetchPlayerLayout());
  }
}
